import Cookies from "js-cookie";

export const AC_TENANT_ID_COOKIE = "AC_TENANT_ID";
export const X_TENANT_ID_HEADER = "x-tenant-id";

/** Host iframe query param for the active Assessment Center / tab tenant. */
export const CURRENT_TENANT_ID_QUERY_PARAM = "currentTenantId";

const CURRENT_TENANT_ID_SESSION_KEY = "aihub.currentTenantId";

function readSessionTenantId(): string {
  try {
    if (typeof window === "undefined") return "";
    return String(
      window.sessionStorage.getItem(CURRENT_TENANT_ID_SESSION_KEY) ?? "",
    ).trim();
  } catch {
    return "";
  }
}

function persistSessionTenantId(tenantId: string): void {
  try {
    if (typeof window === "undefined") return;
    const trimmed = tenantId.trim();
    if (!trimmed) {
      window.sessionStorage.removeItem(CURRENT_TENANT_ID_SESSION_KEY);
      return;
    }
    window.sessionStorage.setItem(CURRENT_TENANT_ID_SESSION_KEY, trimmed);
  } catch {
    // ignore storage failures (privacy mode, blocked storage, etc.)
  }
}

/** Read `currentTenantId` from the iframe URL (or an explicit href). */
export function readCurrentTenantIdFromUrl(href?: string): string | null {
  try {
    if (typeof window === "undefined" && !href) return null;
    const url = href
      ? new URL(
          href,
          typeof window !== "undefined"
            ? window.location.origin
            : "http://localhost",
        )
      : new URL(window.location.href);
    const value = url.searchParams.get(CURRENT_TENANT_ID_QUERY_PARAM)?.trim();
    return value || null;
  } catch {
    return null;
  }
}

/**
 * Assessment Center / AI Hub tenant id for `x-tenant-id`.
 * Priority: iframe `?currentTenantId=` > sessionStorage (last URL value) >
 * `AC_TENANT_ID` cookie (legacy host sync).
 */
export function getAcTenantId(): string {
  const fromUrl = readCurrentTenantIdFromUrl();
  if (fromUrl) {
    persistSessionTenantId(fromUrl);
    return fromUrl;
  }

  const fromSession = readSessionTenantId();
  if (fromSession) return fromSession;

  return String(Cookies.get(AC_TENANT_ID_COOKIE) ?? "").trim();
}

/** Clears session-persisted tenant id (tests). */
export function clearAcTenantIdSessionForTests(): void {
  persistSessionTenantId("");
}

/**
 * Watch tenant id changes (URL / session / cookie via poll + focus/visibility).
 * Cookies and host iframe query updates have no reliable change event.
 */
export function subscribeAcTenantId(
  listener: (next: string, prev: string) => void,
  options?: { intervalMs?: number },
): () => void {
  let last = getAcTenantId();
  const intervalMs = options?.intervalMs ?? 1000;

  const check = () => {
    const next = getAcTenantId();
    if (next === last) return;
    const prev = last;
    last = next;
    listener(next, prev);
  };

  const intervalId = window.setInterval(check, intervalMs);
  window.addEventListener("focus", check);
  window.addEventListener("popstate", check);
  document.addEventListener("visibilitychange", check);

  return () => {
    window.clearInterval(intervalId);
    window.removeEventListener("focus", check);
    window.removeEventListener("popstate", check);
    document.removeEventListener("visibilitychange", check);
  };
}

/** True when the request targets auto-tagging / AI Plan APIs (legacy URL helper). */
export function isAutoTaggingApiUrl(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes("/auto-tagging") ||
    url.includes("/assessment") ||
    url.includes("/knowledge_manager/concepts")
  );
}
